import React, { Fragment } from 'react'
import generalController from '../../../../controllers/generalController'
import { languageController } from '../../../../controllers/language/languageController'
import { Svg } from '../../../1_Atoms/Svg/Svg'
import Link from '../../../1_Atoms/Link/Link'
import { stripHTML, convertCurrency, convertKM } from '../../../../utils/helpers';
import ImagesRotator from '../../../2_Molecules/ImagesRotator/ImagesRotator'
import ReactHtmlParser from 'react-html-parser';

interface CurrentVehicleData {
  tradeInId: number | string
  tradeInMake: string
  tradeInModel: string
  tradeInYear: number
  tradeInTrim: string
  tradeInPriceLow: number
  tradeInPriceHigh: number
  searchTerm: string
  tradeInOdometer: number | string
}

interface DesiredVehicleData {
  id?: number | string
  year?: number | string
  model?: string
  title?: string
  description?: string
  msrp?: number
  visual?: {
    static_content_base_path: string
    jelly_path: string
    jelly: string
    interiors: Array<{
      code: string
      default_exterior: {
        code: string
      }
      include_in_360?: boolean
    }>
    exteriors: {
      [key: string]: {
        '360': {
          start_index: number
          end_index: number
          file_path: string
          filename_prefix: string
          filename_suffix: string
        }
      }
    }
  }
}

interface ValueEstimateStepProps {
  currentVehicleData: CurrentVehicleData | null
  desiredVehicleData: DesiredVehicleData | null
  refreshSteps: () => void
  language: string
  buildAndPriceCtaLink: string
}

interface ImageData {
  src: string
}

declare global {
  interface Window {
    bnp_retailer_mode?: boolean
  }
}

const ValueEstimateStep: React.FC<ValueEstimateStepProps> = ({
  currentVehicleData,
  desiredVehicleData,
  refreshSteps,
  language,
  buildAndPriceCtaLink
}) => {
  const today = new Date();
  const monthLongName = today.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-US', { month: 'long' });
  let priceRangeCopy = '';
  let tradeInValueBnP: number;

  const urlParams = generalController.getURLSearchParams();

  if (!currentVehicleData) {
    return null;
  }

  // If both low and high are more than 0
  if (currentVehicleData.tradeInPriceLow > 0 && currentVehicleData.tradeInPriceHigh > 0) {
    priceRangeCopy = `${convertCurrency(currentVehicleData.tradeInPriceLow , language as 'en' | 'fr')} - ${convertCurrency(currentVehicleData.tradeInPriceHigh, language as 'en' | 'fr')}`
    tradeInValueBnP = currentVehicleData.tradeInPriceLow;
  }
  // If low is more than 0 but high is 0 or minus
  else if (currentVehicleData.tradeInPriceLow > 0 && currentVehicleData.tradeInPriceHigh <= 0) {
    priceRangeCopy = `${convertCurrency(currentVehicleData.tradeInPriceLow, language as 'en' | 'fr')}`
    tradeInValueBnP = currentVehicleData.tradeInPriceLow;
  }
  // If high is more than 0 but low is 0 or minus
  else if (currentVehicleData.tradeInPriceHigh > 0 && currentVehicleData.tradeInPriceLow <= 0) {
    priceRangeCopy = `${convertCurrency(currentVehicleData.tradeInPriceHigh, language as 'en' | 'fr')}`
    tradeInValueBnP = currentVehicleData.tradeInPriceHigh;
  }else{
    priceRangeCopy = `${convertCurrency(0, language as 'en' | 'fr')}`
    tradeInValueBnP = 0;
  }

  const currentDateLabel = (language === 'fr' ?
    `${today.getDate()} ${monthLongName} ${today.getFullYear()}` : `${monthLongName} ${today.getDate()}, ${today.getFullYear()}`);

  // Generate an image path for jelly.
  const generateImageSrc = (vehicleData: DesiredVehicleData | null): string => {
    if (!vehicleData || !vehicleData.visual) return '';
    const { static_content_base_path, jelly_path, jelly } = vehicleData.visual;
    return static_content_base_path + jelly_path + jelly;
  }

  if(window.bnp_retailer_mode){
    window.postMessage(
      {
          type: "setTradeInVal",
          origin: 'form',
          message: tradeInValueBnP,
      },
      "*"
  )
  }else{
    sessionStorage.setItem('TradeInValue', String(tradeInValueBnP));
  }

  // Create a BNP link of the vehicle.
  const createBnpLink = (vehicleData: DesiredVehicleData | null): string => {
    //tradeInValueBnP
    const basePath= language === 'fr' ? process.env.REACT_APP_BNP_PAGE_PATH_FR : process.env.REACT_APP_BNP_PAGE_PATH;
    const modelUrlSlug = vehicleData?.model?.replace(' ', '-');
    const bnpParam = '?tve=' + tradeInValueBnP;

    //console.log(vehicleData, basePath, modelUrlSlug.toLowerCase(), urlParams['bnp'] === '1')
    
    if (basePath && modelUrlSlug && urlParams['bnp'] !== '1') {
      return '/' + language + basePath + '/#/' + vehicleData?.year + '/' + modelUrlSlug.toLowerCase() + bnpParam;
    }else{
      return (sessionStorage.getItem('curr_BNP_URL') || '') + bnpParam;
    }
  }

  // Create a garage page link of the vehicle.
  const createGarageLink = (vehicleData: DesiredVehicleData | null): string => {
    const basePath = language === 'fr' ? process.env.REACT_APP_GARAGE_PAGE_PATH_FR : process.env.REACT_APP_GARAGE_PAGE_PATH;
    const modelUrlSlug = vehicleData?.model;
    if (basePath && modelUrlSlug) {
      return '/' + language + basePath + modelUrlSlug.toLowerCase();
    }
    return '';
  }

  // Generate an array of external images for 360 viewer.
  const generateExteriorImages = (visual: DesiredVehicleData['visual']): ImageData[] => {
    if (!visual) return [];

    try {
      const { interiors, exteriors, static_content_base_path } = visual;

      const defaultExteriorCode = interiors[0]?.default_exterior.code;
      const interior = interiors[0];
      const intCode = interior?.code;

      if (defaultExteriorCode && exteriors[defaultExteriorCode]) {
        const threeSixtyData = exteriors[defaultExteriorCode]['360'];

        const { start_index, end_index, file_path, filename_prefix, filename_suffix } = threeSixtyData;
        let imagesArr: ImageData[] = [];

        for (let index = start_index; index <= end_index; index++) {
          let path: string;

          if(interior && interior.include_in_360)
            path = static_content_base_path + file_path + `${filename_prefix.replace(" ", "-")}${intCode}-` + index + filename_suffix;
          else
            path = static_content_base_path + file_path + filename_prefix + index + filename_suffix;

          imagesArr.push({ src: path });
        }

        return imagesArr;
      }
    } catch (err) {
      console.log(err)
    }
    
    return [];
  }

  return (
    <div className="mz-value-estimate-step-tie">
      <div className="mz-container">
        <h5 className="mz-value-estimate-step-tie__title grid-inner-content">{languageController.getCopy('tradeInEstimator', 'step5Title')}</h5>
        {
          currentVehicleData && (
            <div className="mz-estimate-callout grid-inner-content">
              <Svg type="trade_in_outlined" size={4} />
              <div className="mz-estimate-callout__top">
                <h1>{priceRangeCopy}</h1>
                <p className="label label--tight text--grey">{languageController.getCopy('global', 'asOf')} {currentDateLabel}</p>
              </div>
              <div className="mz-estimate-callout__middle">
                <p className="upper medium ls-md">{languageController.getCopy('tradeInEstimator', 'step5EstimateCopy1')}</p>
                <p className="medium mz-estimate-callout__current-vehicle-label">
                  <span className="upper">{currentVehicleData.tradeInYear} {currentVehicleData.tradeInMake} {currentVehicleData.tradeInModel} {currentVehicleData.tradeInTrim}</span>
                  <br />
                  {convertKM(Number(currentVehicleData.tradeInOdometer), language as 'en' | 'fr')}
                </p>
                {window.bnp_retailer_mode &&
                    <a className="mz-btn mz-btn--secondary" style={{left: '50%',
                      transform: 'translateX(-50%)'}}
                      onClick={(e)=>{
                          e.preventDefault();
                          window.postMessage(
                            {
                                type: "formEvent",
                                origin: 'form',
                                message: 'close',
                            },
                            "*"
                        )
                      }}><span>
                        {languageController.getCopy('tradeInEstimator', 'backToBuild')}
                    </span></a>
                }
                {!window.bnp_retailer_mode &&
                  <a
                    className="mz-estimate-callout__start-over-link mz-link mz-link--light-grey font-small ls-normal"
                    onClick={refreshSteps}
                    data-menu-name="Edit Details"
                    data-form-event="Edit details"
                  >
                    {languageController.getCopy('tradeInEstimator', 'step5EditDetails')}
                  </a>
                }
              </div>
              {
                buildAndPriceCtaLink && (
                  <Fragment>
                    <p className="upper medium ls-md">{languageController.getCopy('tradeInEstimator', 'step5Estimate1')} {desiredVehicleData ? ReactHtmlParser(desiredVehicleData.title || '') : ''} {languageController.getCopy('tradeInEstimator', 'step5Estimate2')}</p>
                    {!window.bnp_retailer_mode &&
                      <Link
                        className="mz-btn mz-btn--primary"
                        text={languageController.getCopy('global', 'buildAndPrice') || ''}
                        href={createBnpLink(desiredVehicleData)}
                        data-menu-name="Build and Price"
                        data-form-event="Visit Build and Price"
                      />
                    }
                  </Fragment>
                )
              }
            </div>
          )
        }
        {!window.bnp_retailer_mode &&
            <div className="mz-value-estimate-step-tie__vehicle grid-inner-content grid-inner-3-8-lg">
                <h4 className="mz-value-estimate-step-tie__vehicle-title">{languageController.getCopy('tradeInEstimator', 'step5Title2')}</h4>
                {
                  desiredVehicleData && (
                    <div className="mz-value-estimate-step-tie__vehicle-content">
                      <div className="mz-value-estimate-step-tie__vehicle-image">
                        <ImagesRotator images={generateExteriorImages(desiredVehicleData.visual)} />
                      </div>
                      <div className="mz-value-estimate-step-tie__vehicle-summary">
                        <h3 className="mz-value-estimate-step-tie__vehicle-name">{desiredVehicleData.year} {ReactHtmlParser(desiredVehicleData.title || '')}</h3>
                        <p className="mz-value-estimate-step-tie__vehicle-description">
                          {desiredVehicleData.description ? stripHTML(desiredVehicleData.description) : ''}
                          {desiredVehicleData.msrp && (
                            <span className="mz-value-estimate-step-tie__vehicle-description-price"> {languageController.getCopy('global', 'pricePrefix')} {convertCurrency(desiredVehicleData.msrp, language as 'en' | 'fr')}
                            </span>
                          )}
                        </p>
                        {/* Drives the user to the corresponding garage page */}
                        <Link
                          className="mz-btn mz-btn--secondary"
                          text={languageController.getCopy('global', 'learnMore') || ''}
                          href={createGarageLink(desiredVehicleData)}
                          data-menu-name="Learn More"
                          data-form-event="Visit Garage page"
                        />
                      </div>
                    </div>
                  )
                }
            </div>
          }
      </div>
    </div>
  )
}

export default ValueEstimateStep
